import { useCallback, useMemo, useState } from "react";

import type { ManagementReservationSearchTypeForQueryParams } from "@/routes/_constants/query-params/management";

import { FeatureIslandContainer, FeatureIslandMainWithBackGround } from "@/components/features/_layouts";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion/accordion";

import { ReservationSearchHeaderAndTab, SearchResultTable } from "../../_shared";
import * as Model from "../model";
import { AboutForSearchResultTable } from "./components";
import {
	BasicSearchActionsComposition,
	SearchByDetailConditionsComposition,
	SearchByReservationCondition,
} from "./compositions";

type Props = {
	hotelID: string;
	reservationSearchType: ManagementReservationSearchTypeForQueryParams;
	startDate: string;
	endDate: string;
	reservationID: string;
};

export const ManagementReservationSearchDefault = ({
	hotelID,
	reservationSearchType,
	startDate,
	endDate,
	reservationID,
}: Props) => {
	/**
	 * 初期化
	 */
	const {
		//
		// readonly
		viewData,
		//
		// 検索条件
		queryVariables,
		//
		// searchable
		searchModel,
		// 詳細検索のフリーワードテキストの変更（空白区切り）
		onChangeFreeWordText,
		// 予約の検索
		onSearchReservationList,
		// 詳細検索のアクティブな予約フラグの変更
		onChangeIsActiveChecked,
		// 詳細検索のキャンセル済みフラグの変更
		onChangeIsCanceledChecked,
		// 詳細検索のメルマガ配信希望者フラグの変更
		onChangeIsAcceptMailMagazineChecked,
		// 詳細検索の現地決済者フラグの変更
		onChangeIsLocalPaymentChecked,
		// 詳細検索の直接入力した予約フラグの変更
		onChangeIsHostReservationChecked,
		// 詳細検索の日帰り予約フラグの変更
		onChangeIsDayTripChecked,
		// 詳細検索の予約内容/プランでの検索条件の追加
		onAddReservationPlans,
		// 詳細検索の予約内容/プランでの検索条件の削除
		onRemoveReservationPlans,
		// 詳細検索の予約内容/部屋での検索条件の追加
		onAddReservationRooms,
		// 詳細検索の予約内容/部屋での検索条件の削除
		onRemoveReservationRooms,
		// 詳細検索の予約内容/オプションでの検索条件の追加
		onAddReservationOptions,
		// 詳細検索の予約内容/オプションでの検索条件の削除
		onRemoveReservationOptions,
		// 詳細検索の予約内容/ディスカウントでの検索条件の追加
		onAddReservationDiscounts,
		// 詳細検索の予約内容/ディスカウントでの検索条件の削除
		onRemoveReservationDiscounts,
	} = Model.UseCase.useInitialized({ hotelID, reservationSearchType, startDate, endDate, reservationID });

	/**
	 * 詳細検索セクションの表示制御
	 */
	const canShowDetailSearchSection = useMemo(() => {
		return viewData.detailSearch.canShowDetailSearchSection;
	}, [viewData.detailSearch.canShowDetailSearchSection]);

	const ACCORDION_ID = "reservation";
	const initialOpenAccordion = useMemo(() => {
		// 1つでも選択されている場合
		if (searchModel.reservation.plans.some((item) => item.checked)) return ACCORDION_ID;
		if (searchModel.reservation.rooms.some((item) => item.checked)) return ACCORDION_ID;
		if (searchModel.reservation.options.some((item) => item.checked)) return ACCORDION_ID;
		if (searchModel.reservation.discounts.some((item) => item.checked)) return ACCORDION_ID;

		return undefined;
	}, [
		searchModel.reservation.discounts,
		searchModel.reservation.options,
		searchModel.reservation.plans,
		searchModel.reservation.rooms,
	]);
	const [openAccordion, setOpenAccordion] = useState<string | undefined>(initialOpenAccordion);
	const handleOpenAccordion = useCallback((value: string) => {
		setOpenAccordion(value);
	}, []);

	return (
		<FeatureIslandContainer>
			<ReservationSearchHeaderAndTab hotelID={hotelID} />

			<FeatureIslandMainWithBackGround>
				<div className="space-y-8">
					{/* 通常検索セクション */}
					<BasicSearchActionsComposition
						queryVariables={queryVariables}
						onSearchReservationList={onSearchReservationList}
					/>

					{/* 絞り込みセクション */}
					{canShowDetailSearchSection && (
						<div className="mt-4 space-y-2">
							{/* 詳細での検索 */}
							<SearchByDetailConditionsComposition
								searchModel={searchModel}
								// 詳細検索のフリーワードテキストの変更（空白区切り）
								onChangeFreeWordText={onChangeFreeWordText}
								// 詳細検索のアクティブな予約フラグの変更
								onChangeIsActiveChecked={onChangeIsActiveChecked}
								// 詳細検索のキャンセル済みフラグの変更
								onChangeIsCanceledChecked={onChangeIsCanceledChecked}
								// 詳細検索のメルマガ配信希望者フラグの変更
								onChangeIsAcceptMailMagazineChecked={onChangeIsAcceptMailMagazineChecked}
								// 詳細検索の現地決済者フラグの変更
								onChangeIsLocalPaymentChecked={onChangeIsLocalPaymentChecked}
								// 詳細検索の直接入力した予約フラグの変更
								onChangeIsHostReservationChecked={onChangeIsHostReservationChecked}
								// 詳細検索の日帰り予約フラグの変更
								onChangeIsDayTripChecked={onChangeIsDayTripChecked}
							/>

							{/* 予約内容での検索 */}
							<Accordion
								type="single"
								collapsible
								value={openAccordion}
								onValueChange={handleOpenAccordion}
							>
								<AccordionItem value={ACCORDION_ID}>
									<AccordionTrigger>予約内容で検索</AccordionTrigger>

									<AccordionContent>
										<div className="space-y-3">
											{/* プラン */}
											<SearchByReservationCondition
												title="プラン"
												options={searchModel.reservation.plans}
												onAddOption={onAddReservationPlans}
												onRemoveOption={onRemoveReservationPlans}
											/>

											{/* 部屋 */}
											<SearchByReservationCondition
												title="部屋"
												options={searchModel.reservation.rooms}
												onAddOption={onAddReservationRooms}
												onRemoveOption={onRemoveReservationRooms}
											/>

											{/* オプション */}
											<SearchByReservationCondition
												title="オプション"
												options={searchModel.reservation.options}
												onAddOption={onAddReservationOptions}
												onRemoveOption={onRemoveReservationOptions}
											/>

											{/* ディスカウント */}
											<SearchByReservationCondition
												title="ディスカウント"
												options={searchModel.reservation.discounts}
												onAddOption={onAddReservationDiscounts}
												onRemoveOption={onRemoveReservationDiscounts}
											/>
										</div>
									</AccordionContent>
								</AccordionItem>
							</Accordion>
						</div>
					)}

					{/* 検索結果テーブル */}
					<div className="mt-10">
						<SearchResultTable
							aboutChildren={<AboutForSearchResultTable viewData={viewData} />}
							rows={viewData.searchResultTableData.rows}
						/>
					</div>
				</div>
			</FeatureIslandMainWithBackGround>
		</FeatureIslandContainer>
	);
};
